/**
 * Запуск оболочки: язык, стор, интерфейс, Telegram WebApp, пауза в фоне, вход. Возвращает то, что нужно циклу боя
 * в main.tsx: можно ли сейчас делать тики и куда класть снимок.
 */
import type { BattleSnapshot, Locale } from "@newfungame/contracts";
import type { Field } from "../field/field";
import { createElement, render } from "preact";
import type { ApiTransport } from "../api";
import { detectLocale, getLocale, localeFromPath, pathForLocale, setLocale } from "../i18n";
import { App } from "./App";
import { createBackgroundWatcher, type BackgroundWatcher } from "./lifecycle";
import { readLanding } from "./landing";
import { analytics } from "../analytics";
import { INSETS_KEY, analyticsPlatform, applyAppInsets, readLaunch, sessionStore, type LaunchInfo } from "./launch";
import { exitRun, signIn } from "./session";
import {
  appStore,
  battleStore,
  initialAppState,
  isBattlePaused,
  readBattlePrefs,
  setPaused,
  type Insets,
} from "./store";
import type { FeedbackInput } from "../ui/windows/feedback/actions";
import {
  appInsets,
  applyClosingConfirmation,
  applyThemeColors,
  disableSwipes,
  expandMiniApp,
  loadTelegramWebApp,
  requestFullscreenOnce,
  type TelegramWebApp,
} from "./telegram";
import { PAGE_META } from "./seo";
import { watchTestAccount } from "./test-account";
import { attachBrowserFullscreen, attachTelegramFullscreen } from "./fullscreen";

/** Выбор языка игрока в браузере (переключатель языка). */
export const LOCALE_KEY = "nfg:locale";

export interface AppController {
  launch: LaunchInfo;
  /** Бой может идти: не свёрнуто и нет модального окна. */
  isBattleRunning(): boolean;
  /** Последний снимок боя — для HUD и подтверждения закрытия. */
  pushSnapshot(s: BattleSnapshot): void;
  /** Сменить язык (переключатель): стор, `<html lang>`, адрес страницы, выбор на устройстве и у игрока на сервере. */
  changeLocale(locale: Locale): void;
  sendFeedback(input: FeedbackInput): Promise<void>;
  /**
   * Подключить поле (C1): отступы поля = место, которое занимают вырез, системные панели и кнопки Telegram
   * (`--app-inset-*` и `env(safe-area-inset-*)` — измеряется по корню интерфейса), обновляются при их смене и ресайзе.
   */
  attachField(field: Pick<Field, "setInsets">): void;
  /** Telegram WebApp, когда загрузится (в браузере — `null`). */
  telegram: Promise<TelegramWebApp | null>;
  dispose(): void;
}

function localStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readSavedLocale(): string | null {
  try {
    return localStore()?.getItem(LOCALE_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Адрес меняется через `history`, HTML страницы остаётся прежним: язык, заголовок и описание ставим сами. */
function applyPageLanguage(locale: Locale): void {
  document.documentElement.lang = locale;
  document.title = PAGE_META[locale].title;
  document.querySelector('meta[name="description"]')?.setAttribute("content", PAGE_META[locale].description);
}

function syncPath(locale: Locale): void {
  const path = pathForLocale(location.pathname, locale);
  if (path !== location.pathname) history.replaceState(history.state, "", path + location.search + location.hash);
}

export function startApp(opts: { api: ApiTransport; root: HTMLElement }): AppController {
  const { api, root } = opts;
  const launch = readLaunch({
    search: location.search,
    hash: location.hash,
    bootInitData: window.__nfgInitData ?? null,
    storage: sessionStore(),
  });
  const { locale, source } = detectLocale({
    pathname: location.pathname,
    miniApp: launch.miniApp,
    telegramLanguage: launch.telegramLanguage,
    saved: readSavedLocale(),
    acceptLanguages: navigator.languages ?? [navigator.language],
  });
  setLocale(locale);
  applyPageLanguage(locale);
  // в браузере адрес совпадает с языком (EN — корень, RU — /ru, UK — /uk); в Mini App адрес не трогаем
  if (!launch.miniApp && (source === "saved" || source === "accept-language")) syncPath(locale);

  appStore.set(
    initialAppState({
      locale,
      insets: readCssInsets(document.documentElement),
      miniApp: launch.miniApp,
      startParam: launch.startParam,
      landing: readLanding(location.search, document.referrer, location.hostname, navigator.webdriver === true),
      platform: analyticsPlatform(launch.miniApp, launch.telegramPlatform),
      ...readBattlePrefs(),
    }),
  );
  render(createElement(App, {}), root);

  const cleanups: (() => void)[] = [];
  const watcher: BackgroundWatcher = createBackgroundWatcher({
    onPause: () => {
      setPaused("hidden", true);
      analytics().appHidden();
    },
    onResume: () => {
      setPaused("hidden", false);
      analytics().appVisible();
    },
    onExit: () => void exitRun(api, "backgroundTimeout"),
  });
  const onVisibility = () => watcher.setHidden("document", document.visibilityState === "hidden");
  document.addEventListener("visibilitychange", onVisibility);
  cleanups.push(
    () => document.removeEventListener("visibilitychange", onVisibility),
    () => watcher.dispose(),
  );
  onVisibility();

  cleanups.push(watchTestAccount());

  const telegram = launch.miniApp ? loadTelegramWebApp() : Promise.resolve(null);
  void telegram.then((tg) => {
    if (tg) cleanups.push(attachTelegram(tg, watcher), attachTelegramFullscreen(tg));
  });
  if (!launch.miniApp) cleanups.push(attachBrowserFullscreen());

  const applyLocale = (next: Locale): void => {
    setLocale(next);
    applyPageLanguage(next);
    appStore.set((s) => ({ ...s, locale: next }));
    if (launch.miniApp) return;
    try {
      localStore()?.setItem(LOCALE_KEY, next);
    } catch {
      // без localStorage выбор держится на сервере
    }
    if ((localeFromPath(location.pathname) ?? "en") !== next) syncPath(next);
  };
  // язык, выбранный раньше на любом устройстве, важнее языка Telegram и браузера; ссылка на /ru, /uk важнее его
  let serverLocaleSeen = source === "path";
  cleanups.push(
    appStore.subscribe((s) => {
      const me = s.player?.player;
      if (serverLocaleSeen || !me) return;
      serverLocaleSeen = true;
      if (me.localeChosen && me.locale !== getLocale()) queueMicrotask(() => applyLocale(me.locale));
    }),
  );

  void signIn(api, launch, locale);

  return {
    launch,
    telegram,
    isBattleRunning: () => !isBattlePaused(appStore.get()),
    pushSnapshot: (s) => battleStore.set(s),
    attachField(field) {
      const measure = () => field.setInsets(measureInsets(root));
      measure();
      let last = appStore.get().insets;
      const off = appStore.subscribe((s) => {
        if (s.insets === last) return;
        last = s.insets;
        // CSS-переменные уже стоят, но раскладка обновится к следующему кадру
        requestAnimationFrame(measure);
      });
      window.addEventListener("resize", measure);
      cleanups.push(off, () => window.removeEventListener("resize", measure));
    },
    changeLocale(next) {
      applyLocale(next);
      serverLocaleSeen = true;
      api.call("setLocale", { locale: next }).catch(() => {
        // нет сети или нет сессии: в браузере выбор остался на устройстве, в Mini App спросим язык Telegram
      });
    },
    async sendFeedback(input) {
      const s = appStore.get();
      await api.call("sendFeedback", {
        ...input,
        wave: s.player?.run.wave ?? null,
        platform: analyticsPlatform(launch.miniApp, launch.telegramPlatform),
        locale: s.locale,
      });
      analytics().feedbackSent(input.type);
    },
    dispose() {
      for (const c of cleanups.splice(0)) c();
    },
  };
}

/** `--app-inset-*` с `<html>` (их до отрисовки ставит BOOT_SCRIPT из кеша сессии). */
function readCssInsets(html: HTMLElement): Insets {
  const cs = getComputedStyle(html);
  const px = (side: string) => Math.max(0, parseFloat(cs.getPropertyValue(`--app-inset-${side}`)) || 0);
  return { top: px("top"), bottom: px("bottom"), left: px("left"), right: px("right") };
}

/** Отступы корня интерфейса от краёв окна: `--app-inset-*` вместе с `env(safe-area-inset-*)` (CSS `.app`). */
function measureInsets(root: HTMLElement): Insets {
  const el = root.firstElementChild ?? root;
  const r = el.getBoundingClientRect();
  const clamp = (v: number) => Math.max(0, Math.round(v));
  // колонки десктоп-раскладки (C6): `--desk-inset-*` — зарегистрированные `<length>`, в computed style уже px
  const cs = getComputedStyle(el);
  const desk = (side: string) => parseFloat(cs.getPropertyValue(`--desk-inset-${side}`)) || 0;
  return {
    top: clamp(r.top),
    left: clamp(r.left + desk("left")),
    right: clamp(window.innerWidth - r.right + desk("right")),
    bottom: clamp(window.innerHeight - r.bottom),
  };
}

/** Бой идёт прямо сейчас: не на паузе и волна не закончилась (между волнами закрыть можно без вопроса). */
function inBattle(): boolean {
  const snap = battleStore.get();
  return !isBattlePaused(appStore.get()) && (snap?.wave.phase === "fight" || snap?.wave.phase === "intro");
}

function attachTelegram(tg: TelegramWebApp, watcher: BackgroundWatcher): () => void {
  const html = document.documentElement;
  expandMiniApp(tg);
  disableSwipes(tg);
  html.dataset.miniapp = "1";

  const syncInsets = () => {
    const insets = appInsets(tg);
    if (!insets) return;
    applyAppInsets(html, insets);
    appStore.set((s) => ({ ...s, insets }));
    try {
      sessionStore()?.setItem(INSETS_KEY, JSON.stringify(insets));
    } catch {
      // без sessionStorage следующая загрузка подождёт Telegram
    }
  };
  const onDeactivated = () => watcher.setHidden("telegram", true);
  const onActivated = () => watcher.setHidden("telegram", false);
  const events: [string, () => void][] = [
    ["safeAreaChanged", syncInsets],
    ["contentSafeAreaChanged", syncInsets],
    ["fullscreenChanged", syncInsets],
    ["deactivated", onDeactivated],
    ["activated", onActivated],
  ];
  for (const [e, fn] of events) {
    try {
      tg.onEvent?.(e, fn);
    } catch {
      // старый клиент без событий
    }
  }
  syncInsets();
  requestFullscreenOnce(tg, sessionStore());
  applyThemeColors(tg, getComputedStyle(html).getPropertyValue("--bg").trim());
  if (tg.isActive === false) onDeactivated();

  // подтверждение закрытия — только пока идёт бой; переключаем лишь на смене, не каждый тик
  let guarded: boolean | null = null;
  const syncGuard = () => {
    const next = inBattle();
    if (next === guarded) return;
    guarded = next;
    applyClosingConfirmation(tg, next);
  };
  const unsubs = [appStore.subscribe(syncGuard), battleStore.subscribe(syncGuard)];
  syncGuard();

  return () => {
    for (const u of unsubs) u();
    for (const [e, fn] of events) {
      try {
        tg.offEvent?.(e, fn);
      } catch {
        // нечего снимать
      }
    }
  };
}
